import Vue from "vue";
import Wkt from "wicket";
import turfArea from "@turf/area";
import turfBbox from "@turf/bbox";
import turfCentroid from "@turf/centroid";
import turfCleanCoords from "@turf/clean-coords";
import turfIntesects from "@turf/boolean-intersects";
import turfKinks from "@turf/kinks";
import turfUnion from "@turf/union";
import gjv from "geojson-validation";
import { coordAll } from "@turf/meta";
import { createNamespacedHelpers } from "vuex-composition-helpers";
import selectApi from "@/api/select";
const uiStore = createNamespacedHelpers("ui");
const cartStore = createNamespacedHelpers("shoppingCart");

export default {
  setup() {
    const { emptyStore } = cartStore.useMutations(["emptyStore"]);
    const { SHOW_SNACKBAR } = uiStore.useMutations(["SHOW_SNACKBAR"]);
    return { SHOW_SNACKBAR, emptyStore };
  },
  methods: {
    // calculates area in square meters
    calculateArea(aois) {
      let sum = 0;

      if (Array.isArray(aois)) {
        aois.forEach((aoi) => {
          if (aoi && aoi.geometry) {
            sum += turfArea(aoi.geometry);
          } else if (aoi && aoi.coordinates) {
            sum += turfArea(aoi);
          }
        });
      }

      return sum;
    },
    hasOverlap(aois) {
      if (Array.isArray(aois) && aois.length > 1) {
        for (let i = 0; i < aois.length; i++) {
          const aoi1 = aois[i];

          for (let k = 0; k < aois.length; k++) {
            const aoi2 = aois[k];

            if (aoi1 !== aoi2) {
              if (turfIntesects(aoi1, aoi2)) {
                return true;
              }
            }
          }
        }
      }
      return false;
    },
    getFeaturesCharacterLength(features) {
      let length = 0;
      if (Array.isArray(features) && features.length > 0) {
        features.forEach((feature) => {
          try {
            const wkt = new Wkt.Wkt();
            wkt.fromObject(
              feature && feature.geometry ? feature.geometry : feature
            );
            length += wkt.toString().length;
          } catch (err) {
            Vue.prototype.$log.error(err);
          }
        });
      }

      return length;
    },
    getVertexCount(features) {
      if (Array.isArray(features) && features.length > 0) {
        const vettedFeatures = [];
        features.forEach((feature) => {
          if (
            feature &&
            feature.type &&
            feature.type.toLowerCase() === "feature"
          ) {
            vettedFeatures.push(feature);
          } else {
            vettedFeatures.push({ type: "Feature", geometry: feature });
          }
        });

        try {
          return coordAll({
            type: "FeatureCollection",
            features: vettedFeatures,
          }).length;
        } catch (err) {
          Vue.prototype.$log.info(
            `Unable to get vertex count: ${err.toString()}`
          );
        }
      }

      return 0;
    },

    calculateKmSquaredArea(aois) {
      const sq_meters = this.calculateArea(aois);
      // convert to km squared
      const sqkm = sq_meters > 0 ? sq_meters / 1000000 : 0;
      return sqkm;
    },

    getIntersectedAois(aois) {
      const intersectingAois = [];

      if (Array.isArray(aois) && aois.length > 0) {
        aois.forEach((aoi) => {
          try {
            const kinks = turfKinks(aoi);

            if (kinks && kinks.features && kinks.features.length > 0) {
              intersectingAois.push(aoi);
            }
          } catch (err) {
            Vue.prototype.$log.info(
              `Error occurred while trying to find kinks in the aoi: ${err.toString()}`
            );
          }
        });
      }

      return intersectingAois;
    },

    getIntersectedAoisCount(aois) {
      return this.getIntersectedAois(aois).length;
    },

    getSmallAois(aois, minimumSize) {
      let result = [];
      if (Array.isArray(aois) && aois.length > 0) {
        aois.forEach((aoi) => {
          // check if geometry is too small
          const geom_area_sqkm = this.calculateKmSquaredArea([aoi]).toFixed(2);

          if (geom_area_sqkm < minimumSize) {
            result.push(aoi);
          }
        });
      }

      return result;
    },

    getSmallAoisCount(aois, minimumSize) {
      return this.getSmallAois(aois, minimumSize).length;
    },

    parseGeom(geom) {
      if (typeof geom === "string") {
        var wkt = new Wkt.Wkt();
        wkt.read(geom);
        geom = wkt.toJson();
      }
      return geom;
    },

    getCleanAois(aois) {
      return aois.filter((aoi) => {
        try {
          turfCleanCoords(aoi);
          return true;
        } catch (err) {
          // console.log(`Found invalid AOI, filtering out invalid AOI: ${err}`);
          return false;
        }
      });
    },

    getIntersects(displayMessageOnError = false) {
      const aois = this.$refs.aoiPanel.aois;
      // console.log(
      //   `checking ${aois.length} aois that they all have clean coordinates`
      // );

      const cleanAOIs = this.getCleanAois(aois);

      if (cleanAOIs.length === 0) {
        if (displayMessageOnError) {
          this.SHOW_SNACKBAR({
            text: `All the AOI's have invalid coordinates.  Please RESET to remove them and check that all the AOI's you add have clean coordinates.`,
            color: "error",
          });
        }
        return null;
      }

      // console.log(`clean aois length ${cleanAOIs.length}`);
      let intersects = cleanAOIs[0];

      if (cleanAOIs.length > 1) {
        // combine all the aois into one multipolygon
        for (let i = 1; i < cleanAOIs.length; i++) {
          intersects = turfUnion(intersects, cleanAOIs[i]);
        }
      }

      return intersects;
    },

    panMap(map, bbox) {
      map.fitBounds(bbox, {
        padding: { top: 50, bottom: 300, left: 350, right: 50 },
        maxZoom: 19,
      });
    },

    isInvalidFeature(feature, limits) {
      if (!feature.geometry) {
        return `Feature does not have any geometry`;
      }

      const geometry = feature.geometry;
      const type = geometry.type ? geometry.type.toLowerCase() : "N/A";
      const allowed_types = ["multipolygon", "polygon"];

      // only MultiPolygon and Polygon are supported
      if (!allowed_types.includes(type)) {
        return `${geometry.type} is NOT a supported`;
      }

      if (!gjv.isFeature(feature)) {
        return `Invalid ${
          type === "polygon" ? "Polygon" : "MultiPolygon"
        } since it is not a valid feature`;
      }

      if (
        Array.isArray(geometry.coordinates) &&
        geometry.coordinates.length > limits.MAX_VERTEX_COUNT
      ) {
        return `${
          type === "polygon" ? "Polygon" : "MultiPolygon"
        } has too many vertices with ${Number(
          geometry.coordinates.length
        ).toLocaleString()}.  Up to ${Number(
          limits.MAX_VERTEX_COUNT
        ).toLocaleString()} vertices are supported`;
      }

      // make sure the value isn't too long
      const wktLen = this.getFeaturesCharacterLength([feature]);

      if (wktLen > limits.MAX_AOI_STRING_LENGTH) {
        return `${
          type === "polygon" ? "Polygon" : "MultiPolygon"
        } is too long with ${Number(
          wktLen
        ).toLocaleString()} characters.  Up to ${Number(
          limits.MAX_AOI_STRING_LENGTH
        ).toLocaleString()} characters are supported`;
      }

      return null;
    },

    convertPolygonToCoordinates(geometry) {
      const centroid = turfCentroid(geometry);
      const centroidLon = centroid.geometry.coordinates[0];
      const centroidLat = centroid.geometry.coordinates[1];

      let topLeft, // -180, 90
        topRight, // 180, 90
        bottomRight, // 180, -90
        bottomLeft; // -180, -90

      geometry.coordinates[0].forEach(([lon, lat]) => {
        if (lon < centroidLon) {
          // left

          if (lat > centroidLat) {
            // top
            topLeft = [lon, lat];
          } else {
            // bottom
            bottomLeft = [lon, lat];
          }
        } else {
          // right

          if (lat > centroidLat) {
            // top
            topRight = [lon, lat];
          } else {
            // bottom
            bottomRight = [lon, lat];
          }
        }
      });

      return {
        topLeft,
        topRight,
        bottomRight,
        bottomLeft,
      };
    },

    addPreviewImages(features, map, layersToMove, beforeId) {
      let sourceAdded = false;
      const previewImageSourceNames = [];

      features.forEach((cell) => {
        const acq_id = cell.properties.acquisition_id;
        const cell_id = cell.properties.cell_id;
        Vue.prototype.$log.debug(
          `adding preview image of cell_id=${cell_id} for acq_id=${acq_id}, cell=${JSON.stringify(
            cell
          )}`
        );

        // const baseUrl = `https://${process.env.VUE_APP_ARD_DOMAIN}/browse-transparent-pngs/preview`;
        const baseUrl = `https://${process.env.VUE_APP_ARD_DOMAIN}/browse/preview`;
        const url = `${baseUrl}/${acq_id}/${cell_id}?format=png`;

        Vue.prototype.$log.debug(
          `cell.geometry.coordinates=${JSON.stringify(
            cell.geometry.coordinates
          )}`
        );

        // image source needs coordinates in the following order: top left, top right, bottom right, bottom left
        // coordinates are in lon (-180 as min to the west, 180 as max to the east), lat (90 as max near the north pole, -90 as min near the south pole)
        const coords = this.convertPolygonToCoordinates(cell.geometry);
        const newCoords = [
          coords.topLeft,
          coords.topRight,
          coords.bottomRight,
          coords.bottomLeft,
        ];

        Vue.prototype.$log.debug(
          `coordinates (top left, top right, bottom right, bottom left)=${newCoords}`
        );
        let sourceName = `${cell_id}-${acq_id}`;
        const source = map.getSource(sourceName);

        if (!source) {
          previewImageSourceNames.push(sourceName);
          map.addSourceLayer(
            sourceName,
            {
              type: "image",
              url: url,
              coordinates: newCoords,
            },
            {
              id: `${sourceName}-id`,
              type: "raster",
              source: `${cell_id}-${acq_id}`,
              paint: {
                "raster-fade-duration": 0,
              },
            },
            beforeId
          );
          sourceAdded = true;
        }
      });

      if (sourceAdded) {
        Vue.prototype.$log.debug(
          `Moving layers since a new preview image source was added...`
        );
        layersToMove.forEach((layer) => {
          map.moveLayer(layer);
        });
      }

      return previewImageSourceNames;
    },

    async load_select_features(view, select_request, select_response) {
      Vue.prototype.$log.debug(`Loading select features...`);
      view.loading_message = "Gathering matching cells";
      view.name = select_response.query_name;
      view.tile_count = select_response.features.length;
      Vue.prototype.$log.debug(
        `Select name=${view.name} has ${view.tile_count} tiles...`
      );
      let covering_cells_params = {};
      if (select_request.intersects) {
        Vue.prototype.$log.debug(
          `we have an intersects aoi=${JSON.stringify(
            select_request.intersects
          )}`
        );
        covering_cells_params = { intersects: select_request.intersects };
      } else if (select_request.bbox) {
        Vue.prototype.$log.debug(
          `we have an bbox aoi=${JSON.stringify(select_request.bbox)}`
        );
        covering_cells_params = { bbox: select_request.bbox };
        // If query does not have an AOI, but has "ids" in criteria
      } else if (select_request.ids) {
        Vue.prototype.$log.debug(
          `we have IDs in the criteria=${JSON.stringify(select_request.ids)}`
        );
        let multipoly = {
          type: "MultiPolygon",
          coordinates: [],
        };
        select_response.features.forEach((feature) => {
          multipoly.coordinates.push(feature.geometry.coordinates);
        });
        covering_cells_params = {
          intersects: multipoly,
        };
        // If query does not have an AOI, but has "cell_id" in criteria
      } else if (select_request.cell_id) {
        Vue.prototype.$log.debug(
          `we have a cell_id criteria=${JSON.stringify(select_request.cell_id)}`
        );
        covering_cells_params = {
          // All features will have the same geometry since it's querying one cell
          intersects: select_response.features[0].geometry,
        };
      }

      try {
        const response = await selectApi.get_covering_cells(
          covering_cells_params
        );
        Vue.prototype.$log.debug(
          `retrieved covering cells=${JSON.stringify(covering_cells_params)}`
        );
        view.covering_cells_features = response.data.features;
        view.loading_message = "Gathering cell metadata";
        view.cell_count = response.data.features.length;

        let datetime_map = {};
        let feature_map = {};
        let select_features = select_response.features;

        Vue.prototype.$log.debug(
          `cell_count=${view.cell_count} containing ${select_features.length} features...`
        );

        // organize features by cell id
        select_features.forEach((feature) => {
          const cell_id = feature.properties.cell_id;
          const acq_id = feature.properties.acquisition_id;
          let feature_data = feature.properties;
          feature_data["geometry"] = feature.geometry;
          if (cell_id in feature_map) {
            feature_map[cell_id].push(feature_data);
          } else {
            feature_map[cell_id] = [feature_data];
          }
          // calculate oldest date per cell
          if (
            cell_id in datetime_map &&
            "datetime_min" in datetime_map[cell_id]
          ) {
            if (
              feature.properties.datetime <
              datetime_map[cell_id]["datetime_min"]
            ) {
              datetime_map[cell_id]["datetime_min"] =
                feature.properties.datetime;
            }
          } else {
            datetime_map[cell_id] = {
              ...datetime_map[cell_id],
              datetime_min: feature.properties.datetime,
            };
          }
          // calculate newest date per cell
          if (
            cell_id in datetime_map &&
            "datetime_max" in datetime_map[cell_id]
          ) {
            if (
              feature.properties.datetime >
              datetime_map[cell_id]["datetime_max"]
            ) {
              datetime_map[cell_id]["datetime_max"] =
                feature.properties.datetime;
            }
          } else {
            datetime_map[cell_id] = {
              ...datetime_map[cell_id],
              datetime_max: feature.properties.datetime,
            };
          }

          // calculate unique acquisitions
          if (!view.unique_acquisitions.find((acq) => acq.id === acq_id)) {
            view.unique_acquisitions.push({
              id: acq_id,
              cell_id,
              time: new Date(feature.properties.datetime).getTime(),
            });
          }
        });

        Vue.prototype.$log.debug(
          `we found ${
            view.unique_acquisitions.length
          } unique_acquisitions=${JSON.stringify(view.unique_acquisitions)}`
        );

        view.loading_message = "Mapping results";

        let result_map = response.data;
        // add tiles to each cell feature properties
        result_map.features.forEach((feature) => {
          const match_length = (feature_map[feature.properties.id] || [])
            .length;
          feature.properties["matches"] =
            feature_map[feature.properties.id] || [];
          // calculate pop up cell metadata
          feature.properties["available_tiles"] = match_length;
          feature.properties["stack_depth_reached"] =
            match_length >= view.stack_depth;
          if (feature.properties.id in datetime_map) {
            feature.properties["datetime_min"] =
              datetime_map[feature.properties.id]["datetime_min"];

            feature.properties["datetime_max"] =
              datetime_map[feature.properties.id]["datetime_max"];
          }
        });

        view.select_features = result_map;
        this.formatSelectResult(view, view.select_features);

        let id = 1;
        // add number id's to the features so we can do hover states, note the string id's are not honored for a feature id on the map
        view.select_features.features.forEach((f) => {
          f.id = id;
          id++;
        });

        Vue.prototype.$log.debug(`adding select_features to the map...`);
        view.map
          .getSource(view.metadataSourceName)
          .setData(view.select_features);

        // zoom to fit the bounds of the metadata or the polygon if we don't have metadata
        const newBounds = turfBbox(result_map);

        Vue.prototype.$log.debug(
          `panning map to extent=${JSON.stringify(newBounds)}`
        );
        // zoom to the extents of the selected grid item
        view.panMap(view.map, newBounds);
      } catch (err) {
        Vue.prototype.$log.error(
          `error occurred while trying to load select feautures: ${err.toString()}`
        );
        throw err;
      } finally {
        view.loading = false;
      }
    },

    formatSelectResult(view, select_features) {
      Vue.prototype.$log.debug(
        `formatting select_feaures=${JSON.stringify(select_features)}`
      );
      this.emptyStore();
      view.formated = [];
      select_features.features.forEach((cell) => {
        const parent = { children: [] };
        parent.id = cell.properties.id;
        parent.name = cell.properties.id;
        cell.properties.matches.forEach((tile) => {
          parent.children.push({
            id: `${tile.cell_id}_${tile.acquisition_id}`,
            name: tile.acquisition_id,
            cell: tile.cell_id,
          });
          view.addToCart({
            cell: tile.cell_id,
            id: `${tile.cell_id}_${tile.acquisition_id}`,
            name: tile.acquisition_id,
          });
        });
        if (parent.children.length > 0) {
          view.formated.push(parent);
        }
      });
    },

    formatNumber(usage) {
      Object.entries(usage[0]).forEach(([key, value]) => {
        if (typeof value === "number") {
          usage[0][key] = value.toLocaleString();
        }
      });
      return usage;
    },

    removePreviewImages(view) {
      view.previewImageSourceNames.forEach((sourceName) => {
        let id = `${sourceName}-id`;
        view.map.removeSourceLayer(sourceName, id);
      });
      view.previewImageSourceNames = [];
    },
  },
};
